import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { AudioDictationService } from 'src/app/shared/services/audio-dictation.service';
import { HttpEventType } from '@angular/common/http';

@Component({
  selector: 'app-multi-file-transcription-modal',
  templateUrl: './multi-file-transcription-modal.component.html',
  styleUrls: ['./multi-file-transcription-modal.component.scss'],
})
export class MultiFileTranscriptionModalComponent implements OnInit {
  selectedFiles: File[] = [];
  responseData: any;
  transcriptionData: any;
  parsedData: any;
  isSubmitting = false;
  uploadProgress: number = 0;
  isProcessing = false;

  @ViewChild('fileInput') fileInput: ElementRef;
  @ViewChild('orders') orders: ElementRef;
  @ViewChild('doctorNotes') doctorNotes: ElementRef;
  @ViewChild('recommendations') recommendations: ElementRef;
  @ViewChild('transcription') transcription: ElementRef;

  constructor(
    public activeModal: NgbActiveModal,
    private spinnerService: NgxSpinnerService,
    private toastr: ToastrService,
    private audioDictationService: AudioDictationService
  ) {}

  ngOnInit(): void {}

  onFileSelected(event: any): void {
    const files = event.target.files;
    if (files) {
      for (let i = 0; i < files.length; i++) {
        this.selectedFiles.push(files[i]);
      }
    }
  }

  removeFile(index: number): void {
    this.selectedFiles.splice(index, 1);
  }

  browseFiles(): void {
    this.fileInput.nativeElement.click();
  }

  submitFiles(): void {
    if (this.selectedFiles.length === 0) {
      this.toastr.error('Please select at least one audio file');
      return;
    }

    this.isSubmitting = true;
    this.uploadProgress = 0;
    this.isProcessing = false;

    const formData = new FormData();
    this.selectedFiles.forEach((file, index) => {
      formData.append('files', file);
    });

    this.audioDictationService
      .getMultipleFilesTranscription(formData)
      .subscribe({
        next: (event) => {
          if (event.type === HttpEventType.UploadProgress && event.total) {
            this.uploadProgress = Math.round(
              (event.loaded / event.total) * 100
            );
            if (this.uploadProgress === 100) {
              this.isProcessing = true;
            }
          } else if (event.type === HttpEventType.Response) {
            this.isSubmitting = false;
            this.uploadProgress = 100;
            this.isProcessing = true;
            const res = event.body;
            if (res && res.data) {
              this.transcriptionData = res.data.transcription;
              try {
                this.responseData = res.data.doctor_notes;
                this.parsedData = JSON.parse(this.responseData);
                this.isProcessing = false;
              } catch (error) {
                console.error('Error parsing JSON:', error);
                this.toastr.error('Failed to parse response data');
                this.parsedData = null;
                this.isProcessing = false;
              }
            } else {
              this.toastr.error('Failed to get transcription data');
              this.isProcessing = false;
            }
          }
        },
        error: (err) => {
          this.isSubmitting = false;
          this.uploadProgress = 0;
          this.isProcessing = false;
          console.error(err);
          this.toastr.error(err.message || 'Failed to transcribe files');
        },
      });
  }

  copySection(section: string): void {
    let textToCopy = '';
    switch (section) {
      case 'orders':
        textToCopy = this.orders?.nativeElement?.innerText || '';
        break;
      case 'doctorNotes':
        textToCopy = this.doctorNotes?.nativeElement?.innerText || '';
        break;
      case 'recommendations':
        textToCopy = this.recommendations?.nativeElement?.innerText || '';
        break;
      case 'transcription':
        textToCopy = this.transcription?.nativeElement?.innerText || '';
        break;
    }
    this.copyTextToClipboard(textToCopy);
  }

  copyAll(): void {
    const ordersText = this.orders?.nativeElement?.innerText || '';
    const doctorNotesText = this.doctorNotes?.nativeElement?.innerText || '';
    const recommendationsText =
      this.recommendations?.nativeElement?.innerText || '';
    const allText = `${ordersText}\n\n${doctorNotesText}\n\n${recommendationsText}`;
    this.copyTextToClipboard(allText);
  }

  private copyTextToClipboard(text: string): void {
    if (!text) {
      this.toastr.error('No content to copy');
      return;
    }

    const textarea = document.createElement('textarea');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    textarea.value = text;
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
    this.toastr.success('Copied to clipboard');
  }
}
